import React, { useEffect, useState } from "react"
import { withRouter } from "react-router-dom"
import MetaTags from "react-meta-tags"
import { Container, Card, CardBody, Row, Col } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import BootstrapTable from "react-bootstrap-table-next"
import TableLoader from "components/Common/TableLoader"
import { sitename } from "constants/constants"
import {
  getAffiliateWithdrawalsCall,
  approveAffiliateWithdrawalCall,
  rejectAffiliateWithdrawalCall,
} from "helpers/repository"
import { showErrorMessages } from "helpers/helpers"
import { showMessage } from "components/Common/Toaster"

const formatMoney = value =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

const StatusBadge = ({ status }) => {
  const value = status?.toLowerCase()
  if (value === "pending") {
    return <span className="badge badge-soft-primary font-size-12">Pending</span>
  }
  if (value === "approved" || value === "success") {
    return <span className="badge badge-soft-success font-size-12">{status}</span>
  }
  if (value === "declined" || value === "rejected") {
    return <span className="badge badge-soft-danger font-size-12">{status}</span>
  }
  return <span className="badge badge-soft-secondary font-size-12">{status || "—"}</span>
}

const EmptyState = () => (
  <div className="text-center py-5">
    <i
      className="bx bx-wallet-alt"
      style={{ fontSize: "48px", color: "#c3cbe4" }}
    />
    <h5 className="mt-3 mb-1">No affiliate withdrawals</h5>
    <p className="text-muted mb-0">
      There are no affiliate withdrawal requests to show right now.
    </p>
  </div>
)

const RowActions = ({ row, onDone }) => {
  const [actionLoading, setActionLoading] = useState(null)

  if (row?.status?.toLowerCase() !== "pending") {
    return <span className="text-muted">—</span>
  }

  const handleAction = async (action, call) => {
    setActionLoading(action)
    try {
      const response = await call(row.id)
      showMessage("success", response?.data?.message)
      onDone()
    } catch (error) {
      showErrorMessages(error)
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <div className="d-flex gap-2">
      <button
        type="button"
        className="btn btn-success btn-sm"
        disabled={!!actionLoading}
        onClick={() => handleAction("approve", approveAffiliateWithdrawalCall)}
      >
        {actionLoading === "approve" ? "Approving..." : "Approve"}
      </button>
      <button
        type="button"
        className="btn btn-danger btn-sm"
        disabled={!!actionLoading}
        onClick={() => handleAction("reject", rejectAffiliateWithdrawalCall)}
      >
        {actionLoading === "reject" ? "Rejecting..." : "Reject"}
      </button>
    </div>
  )
}

const AffiliateWithdrawals = () => {
  const [data, setData] = useState([])
  const [pagination, setPagination] = useState(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const call = async () => {
    setLoading(true)
    try {
      const response = await getAffiliateWithdrawalsCall({ page, per_page: 20 })
      const result = response?.data?.data || {}
      setData(result.data || [])
      setPagination(result.pagination || null)
    } catch (error) {
      showErrorMessages(error)
      setData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    call()
  }, [page])

  const columns = [
    {
      dataField: "user.phone_number",
      text: "Phone Number",
      formatter: (cell, row) => row?.user?.phone_number || row?.phone_number,
    },
    {
      dataField: "user.username",
      text: "Username",
      formatter: (cell, row) => row?.user?.username || "—",
    },
    {
      dataField: "amount",
      text: "Amount",
      formatter: cell => formatMoney(cell),
    },
    {
      dataField: "current_balance",
      text: "Current Balance",
      formatter: cell => formatMoney(cell),
    },
    {
      dataField: "updated_balance",
      text: "Updated Balance",
      formatter: cell => formatMoney(cell),
    },
    { dataField: "category", text: "Category" },
    {
      dataField: "status",
      text: "Status",
      formatter: cell => <StatusBadge status={cell} />,
    },
    {
      dataField: "action",
      text: "Action",
      formatter: (cell, row) => <RowActions row={row} onDone={call} />,
    },
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{sitename}</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs title="Affiliate" breadcrumbItem="Withdrawal Report" />
          <Card>
            <CardBody>
              <div className="mb-4 h4 card-title">{"Withdrawal Report"}</div>
              {loading ? (
                <TableLoader />
              ) : data.length === 0 ? (
                <EmptyState />
              ) : (
                <>
                  <div className="table-responsive">
                    <BootstrapTable
                      keyField="id"
                      responsive
                      bordered={false}
                      striped={false}
                      classes="table align-middle table-nowrap table-check"
                      headerWrapperClasses="table-light"
                      columns={columns}
                      data={data}
                      rowClasses="mytable-hover-row"
                    />
                  </div>
                  {pagination && pagination.last_page > 1 && (
                    <Row className="mt-2">
                      <Col className="d-flex justify-content-end align-items-center gap-2">
                        <span className="text-muted">
                          Page {pagination.current_page} of {pagination.last_page}{" "}
                          ({pagination.total} total)
                        </span>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          disabled={page <= 1}
                          onClick={() => setPage(page - 1)}
                        >
                          Prev
                        </button>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          disabled={page >= pagination.last_page}
                          onClick={() => setPage(page + 1)}
                        >
                          Next
                        </button>
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default withRouter(AffiliateWithdrawals)
